import {useState} from "react";
import {Error, MutationDeleteGatewayArgs, ReferenceRetarget} from "@/api";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {defineMessage} from "react-intl";
import {useNavigate, NavigateFunction} from "react-router";
import {DeleteGatewayMutationHookResult, useDeleteGatewayMutation} from "../queries.generated";

const initialState: MutationDeleteGatewayArgs = {
    gatewayId: null,
    relatedData: {
        users: [],
    },
};

export enum ReferenceType {
    USER = "users",
}

type Selection = {
    [extensionId: number]: number;
};

export default class DeleteGatewayForm {
    domainId: number;
    gatewayId: number;
    values: MutationDeleteGatewayArgs;
    errors: Error[];
    selections: Selection;

    protected _setValues: (data: MutationDeleteGatewayArgs) => void;
    protected setErrors: (errors: Error[]) => void;
    protected setSelections: (selections: Selection) => void;
    protected dispatch: any;
    protected mutation: DeleteGatewayMutationHookResult[0];
    protected navigate: NavigateFunction;

    constructor(domainId: number, gatewayId: number) {
        [this.mutation] = useDeleteGatewayMutation();
        [this.errors, this.setErrors] = useState([]);
        [this.values, this._setValues] = useState(initialState);
        [this.selections, this.setSelections] = useState<Selection>({});
        this.domainId = domainId;
        this.gatewayId = gatewayId;
        this.dispatch = useDispatch();
        this.navigate = useNavigate();
    }

    redirect() {
        this.navigate(`/domains/${this.domainId}/gateways/gateways`);
    }

    updateValues(data: Partial<MutationDeleteGatewayArgs>) {
        this._setValues(Object.assign({}, this.values, data));
    }

    selectNewTarget(referrerId: number, type: ReferenceType, newTargetId: number) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        const found = list.find((e) => e.referrerId == referrerId);
        if (found) found.newTargetNumberId = newTargetId;
        else
            list.push({
                referrerId: referrerId,
                newTargetNumberId: newTargetId,
            });
        this._setValues(Object.assign({}, this.values, this.values));
    }

    deselectTarget(referrerId: number, type: ReferenceType) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        const index = list.findIndex((e) => e.referrerId == referrerId);
        if (index > -1) list.splice(index, 1);
    }

    handleChange(newTarget: number, dataId: number, dataType: ReferenceType): void {
        if (newTarget === 0) {
            this.deselectTarget(dataId, dataType);
        } else {
            this.selectNewTarget(dataId, dataType, newTarget);
        }
        this.setSelections({
            ...this.selections,
            [dataId]: newTarget,
        });
    }

    allSelected(type: ReferenceType, ids: number[]) {
        const list: ReferenceRetarget[] = this.values.relatedData[type];
        for (const id_ of ids) {
            if (!list.find((e) => e.referrerId == id_)) return false;
        }
        return true;
    }

    delete() {
        this.mutation({
            variables: {
                gatewayId: this.gatewayId,
                relatedData: this.values.relatedData,
            },
        })
            .then((result) => {
                if (result.data.deleteGateway.length === 0) {
                    this.dispatch(
                        showNotification(
                            defineMessage({id: "gateway_deleted", defaultMessage: "Gateway deleted."}),
                            "success",
                        ),
                    );
                    this.redirect();
                    return;
                } else if (result.data.deleteGateway.length > 0) {
                    this.setErrors(result.data.deleteGateway);
                    return;
                }

                this.dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            })
            .catch(() => {
                this.dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            });
    }

    reset() {
        this.updateValues(initialState);
        this.setSelections({});
    }
}
